package com.meistercharts.charts.pv.roofPlanning

import com.meistercharts.canvas.command.Command
import it.neckar.geometry.Coordinates
import it.neckar.geometry.Size
import it.neckar.open.unit.si.mm

/**
 * Every change the module planning applies to a [PvRoofPlanningModel].
 *
 * The interaction handling creates these and hands them to a [com.meistercharts.canvas.command.CommandExecutor]; it
 * never changes the model itself. Each command stores what it overwrote, so undo restores exactly the previous state.
 */
typealias RoofPlanningCommand = Command<PvRoofPlanningModel>

/**
 * Moves a module area to a new location.
 *
 * One drag produces one command with the total distance, so undo takes back the whole gesture instead of one mouse move.
 */
class MoveModuleArea(
  val moduleArea: ModuleArea,
  val newLocation: @RoofRelative Coordinates,
  private val previousLocation: @RoofRelative Coordinates = moduleArea.location,
) : RoofPlanningCommand {

  override val description: String = "Modulfeld verschieben"

  override val gestureKey: Any = moduleArea

  override fun mergeWith(previous: Command<PvRoofPlanningModel>): RoofPlanningCommand {
    require(previous is MoveModuleArea) { "Expected a MoveModuleArea but was <$previous>" }
    return MoveModuleArea(moduleArea, newLocation, previous.previousLocation)
  }

  override fun execute(model: PvRoofPlanningModel) {
    moduleArea.location = newLocation
    moduleArea.fillGrid(model.modulesSize)
  }

  override fun undo(model: PvRoofPlanningModel) {
    moduleArea.location = previousLocation
    moduleArea.fillGrid(model.modulesSize)
  }
}

/**
 * Moves an unusable area to a new location. The module areas are refilled, because a module below the new location
 * cannot stay.
 */
class MoveUnusableArea(
  val unusableArea: UnusableArea,
  val newLocation: @RoofRelative Coordinates,
  private val previousLocation: @RoofRelative Coordinates = unusableArea.location,
) : RoofPlanningCommand {

  override val description: String = "Sperrfläche verschieben"

  override val gestureKey: Any = unusableArea

  override fun mergeWith(previous: Command<PvRoofPlanningModel>): RoofPlanningCommand {
    require(previous is MoveUnusableArea) { "Expected a MoveUnusableArea but was <$previous>" }
    return MoveUnusableArea(unusableArea, newLocation, previous.previousLocation)
  }

  override fun execute(model: PvRoofPlanningModel) {
    unusableArea.location = newLocation
    model.updateModuleAreas(model.modulesSize)
  }

  override fun undo(model: PvRoofPlanningModel) {
    unusableArea.location = previousLocation
    model.updateModuleAreas(model.modulesSize)
  }
}

/**
 * Resizes a [Resizable] - a module area or an unusable area - to a new location and size.
 */
class ResizeElement(
  val resizable: Resizable,
  val newLocation: @RoofRelative Coordinates,
  val newSize: @mm Size,
  private val previousLocation: @RoofRelative Coordinates = resizable.location,
  private val previousSize: @mm Size = resizable.size,
) : RoofPlanningCommand {

  override val description: String = when (resizable) {
    is ModuleArea -> "Modulfeld verändern"
    is UnusableArea -> "Sperrfläche verändern"
  }

  override val gestureKey: Any = resizable

  override fun mergeWith(previous: Command<PvRoofPlanningModel>): RoofPlanningCommand {
    require(previous is ResizeElement) { "Expected a ResizeElement but was <$previous>" }
    return ResizeElement(resizable, newLocation, newSize, previous.previousLocation, previous.previousSize)
  }

  override fun execute(model: PvRoofPlanningModel) {
    resizable.location = newLocation
    resizable.size = newSize
    model.updateModuleAreas(model.modulesSize)
  }

  override fun undo(model: PvRoofPlanningModel) {
    resizable.location = previousLocation
    resizable.size = previousSize
    model.updateModuleAreas(model.modulesSize)
  }
}

/**
 * Rotates the modules of a module area by 90 degrees.
 */
class RotateModuleArea(
  val moduleArea: ModuleArea,
) : RoofPlanningCommand {

  /**
   * Rotating refills the grid and forgets which modules the user had deleted by hand - undo puts them back.
   */
  private var deletedModuleIndices: List<ModuleIndex> = emptyList()

  override val description: String = "Modulfeld drehen"

  override fun execute(model: PvRoofPlanningModel) {
    deletedModuleIndices = moduleArea.deletedModuleIndices()
    moduleArea.rotate()
  }

  override fun undo(model: PvRoofPlanningModel) {
    moduleArea.rotate()
    moduleArea.restoreDeletedModules(deletedModuleIndices)
  }
}

/**
 * Deletes a module area including its modules.
 */
class DeleteModuleArea(
  val moduleArea: ModuleArea,
) : RoofPlanningCommand {

  /**
   * Which modules the user had deleted by hand before the area went away - restoring the area has to restore those too.
   */
  private var deletedModuleIndices: List<ModuleIndex> = emptyList()

  private var previousIndex: Int = -1

  override val description: String = "Modulfeld löschen"

  override fun execute(model: PvRoofPlanningModel) {
    previousIndex = model.moduleAreas.moduleAreas.indexOf(moduleArea)
    require(previousIndex >= 0) { "Module area is not part of the model: <$moduleArea>" }

    deletedModuleIndices = moduleArea.deletedModuleIndices()

    moduleArea.clear()
    model.moduleAreas.remove(moduleArea)
  }

  override fun undo(model: PvRoofPlanningModel) {
    moduleArea.fillGrid(model.modulesSize)
    moduleArea.restoreDeletedModules(deletedModuleIndices)
    model.moduleAreas.insert(previousIndex, moduleArea)
  }
}

/**
 * Deletes an unusable area. The module areas are refilled, because the modules the area was blocking fit again.
 */
class DeleteUnusableArea(
  val unusableArea: UnusableArea,
) : RoofPlanningCommand {

  private var previousIndex: Int = -1

  override val description: String = "Sperrfläche löschen"

  override fun execute(model: PvRoofPlanningModel) {
    previousIndex = model.unusableAreas.unusableAreas.indexOf(unusableArea)
    require(previousIndex >= 0) { "Unusable area is not part of the model: <$unusableArea>" }

    model.removeUnusableArea(unusableArea)
  }

  override fun undo(model: PvRoofPlanningModel) {
    model.unusableAreas.insert(previousIndex, unusableArea)
    model.updateModuleAreas(model.modulesSize)
  }
}

/**
 * Deletes a single module of a module area, or restores it if it had been deleted before.
 */
class ToggleModuleDeleted(
  val module: Module,
) : RoofPlanningCommand {

  override val description: String = if (module.deleted) "Modul wiederherstellen" else "Modul löschen"

  override fun execute(model: PvRoofPlanningModel) {
    module.deleted = module.deleted.not()
  }

  override fun undo(model: PvRoofPlanningModel) {
    module.deleted = module.deleted.not()
  }
}
